import { isBoolean, isEmpty, isString, replaceWithNonbreakableSpaces } from '~/utils';

// Local regex patterns (migrated from jl-design-system/form)
const lettersDotDashApostropheSpacePattern = "A-Za-z.\\-'\\s";
const getRegExp = (pattern) => new RegExp(`^[${pattern}]+$`);

const phoneNumberUkMobileRegex = /^(\+44\s?7\d{3}|\(?07\d{3}\)?)\s?\d{3}\s?\d{3}$/;

// Email validation (migrated from jl-design-system/form/fields/email)
const emailRegex =
  /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$/;
const emailValidationErrorMessage = 'Please make sure your email format is correct';

export const DEFAULT_ERROR_MESSAGE = 'This is required';
export const POSTCODE_INVALID_ERROR_MESSAGE = (
  <>
    We can&apos;t find any addresses.
    <br />
    Please enter a valid UK postcode {replaceWithNonbreakableSpaces('(e.g. SW1E 5NN)')}
  </>
);
export const PHONE_NUMBER_UK_MOBILE_INVALID_ERROR_MESSAGE = 'Please enter a UK mobile number';
export const SOMEONE_ELSE_NAME_INVALID_ERROR_MESSAGE =
  'Please remove the invalid characters to continue';
export const SOMEONE_ELSE_NAME_EMPTY_ERROR_MESSAGE = 'Please enter a name';

const GBPostcode = /^[A-Z]{1,2}[0-9R][0-9A-Z]? ?[0-9][ABD-HJLNP-UW-Z]{2}$/;

const getCurrentValue = (value) => (Array.isArray(value) ? value.length !== 0 && value[0] : value);

export const requiredMultiply = (value) =>
  value && value.length !== 0 ? undefined : 'Please choose one or more sizes';
export const requiredAge = (value) =>
  value && value.length > 0 ? undefined : 'Please choose your age range';
export const requiredHeight = (value) =>
  value && (value.feet || value.inches || value.cm) ? undefined : 'Please enter height information';

const requiredValidation = (value, errorMessage = DEFAULT_ERROR_MESSAGE) => {
  const currentValue = getCurrentValue(value);
  if (isString(currentValue)) {
    return currentValue.trim() ? undefined : errorMessage;
  }
  return currentValue ? undefined : errorMessage;
};

export const required = (value) => requiredValidation(value);

export const requiredWithMessage = (value, errorMessage) => requiredValidation(value, errorMessage);

export const validateGBPostcode = (value) =>
  getCurrentValue(value) && value.match(GBPostcode) ? undefined : POSTCODE_INVALID_ERROR_MESSAGE;

export const requiredCheckbox = (value) => {
  const currentValue = getCurrentValue(value);
  return currentValue && isBoolean(currentValue) && currentValue
    ? undefined
    : DEFAULT_ERROR_MESSAGE;
};

export const requiredDropdown = (value) => {
  const currentValue = getCurrentValue(value);
  return currentValue && currentValue !== 'default' ? undefined : DEFAULT_ERROR_MESSAGE;
};

export const validatePhoneNumberUKMobile = (value) =>
  getCurrentValue(value) && getCurrentValue(value).match(phoneNumberUkMobileRegex)
    ? undefined
    : PHONE_NUMBER_UK_MOBILE_INVALID_ERROR_MESSAGE;

export const validateEmail = (value) =>
  getCurrentValue(value) && getCurrentValue(value).match(emailRegex)
    ? undefined
    : emailValidationErrorMessage;

export const validateSomeoneElseName = (value) => {
  const name = getCurrentValue(value);
  if (isEmpty(name)) {
    return SOMEONE_ELSE_NAME_EMPTY_ERROR_MESSAGE;
  }
  return name.match(getRegExp(lettersDotDashApostropheSpacePattern))
    ? undefined
    : SOMEONE_ELSE_NAME_INVALID_ERROR_MESSAGE;
};

export const validateEventDate = (value) => {
  if (value) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const eventDay = new Date(value);
    eventDay.setHours(0, 0, 0, 0);
    return eventDay - today < 0 ? 'Please enter a date in the future' : undefined;
  }
  return undefined;
};

export const validateDateExcess = (getState) => (value) => {
  if (value) {
    const {
      appointment: {
        draft: { dateSlot },
      },
    } = getState();
    const exceedDate = new Date(dateSlot);
    exceedDate.setHours(0, 0, 0, 0);
    exceedDate.setDate(exceedDate.getDate() + 12 * 7);

    const eventDay = new Date(value);
    eventDay.setHours(0, 0, 0, 0);
    return eventDay > exceedDate
      ? 'Event date must be within 12 weeks of your appointment date'
      : undefined;
  }
  return undefined;
};

export const validateDate = (dateValue) => {
  if (dateValue) {
    const currentValue = getCurrentValue(dateValue);
    return currentValue === 'Invalid Date' ? 'Please enter a valid date' : undefined;
  }
  return undefined;
};
